// client/src/components/account/AccountUsers.js
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query'; // Importamos o useQuery direto por segurança
import { useAccountUsers, useMutateAccountUsers } from '../../hooks/useAccounts';
import { useRoles } from '../../hooks/useRoles';
import { useAuth } from '../../context/auth/AuthProvider';
import { isMatrizAdmin, isFranquiaAdmin } from '../../utils/permissions';
import useAlert from '../../hooks/useAlert';
import PermissionGuard from '../routing/PermissionGuard';
import EquipePanel from '../plataforma/EquipePanel';
import api from '../../services/api'; // ✅ Garantia de chamada direta caso o hook falhe em passar query
import {
    UserPlus,
    Users,
    Loader2,
    Filter as FilterIcon,
    X,
    Search,
    Eye,
    Key,
    UserX,
    UserCheck
} from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const AccountUsers = () => {
    const { user: currentUser } = useAuth();
    const { setAlert } = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    
    // ✅ Filtros locais separados dos ativos para permitir o submit
    const initialFilters = { search: '', status: 'todos', role: '' };
    const [localFilters, setLocalFilters] = useState(initialFilters);
    const [activeFilters, setActiveFilters] = useState(initialFilters);
    
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const usersPerPage = 10;

    const getContaIdFromCurrentUser = useCallback(() => {
        if (!currentUser) return null;
        if (currentUser.account) {
            return typeof currentUser.account === 'object' 
                ? currentUser.account._id || currentUser.account.id
                : currentUser.account;
        }
        return null;
    }, [currentUser]);

    const contaId = getContaIdFromCurrentUser();

    // ✅ ABORDAGEM DE SEGURANÇA: Fazemos uma query direta garantindo que o `?status=` vá na URL
    const { 
        data: accountUsersData, 
        isLoading: accountLoading, 
        refetch: refetchUsers 
    } = useQuery({
        queryKey: ['accountUsers', activeFilters.status], // Reage automaticamente quando o status muda
        queryFn: async () => {
            const endpoint = isMatrizAdmin(currentUser) || isFranquiaAdmin(currentUser)
                ? `/contas/${contaId}/users`
                : '/contas/my/users';
                
            // ✅ Força o parâmetro na URL para garantir que o Backend receba
            const statusTarget = activeFilters.status || 'todos';
            const res = await api.get(`${endpoint}?status=${statusTarget}`);
            return res.data;
        },
        enabled: !!currentUser
    });
    
    // Busca o dicionário de roles para cruzar o ID com o Nome
    const { data: rolesData } = useRoles({ account: contaId, enabled: !!contaId });
    const rolesList = useMemo(() => Array.isArray(rolesData) ? rolesData : (rolesData?.data || []), [rolesData]);

    const { 
        resetAccountUserPassword, 
        toggleUserActiveStatus 
    } = useMutateAccountUsers();

    useEffect(() => {
        refetchUsers();
    }, [location.pathname, refetchUsers]);

    useEffect(() => {
        const handleUserDesvinculado = (event) => {
            console.log('🔄 Usuário desvinculado, atualizando lista...', event.detail);
            queryClient.invalidateQueries({ queryKey: ['accountUsers'] });
            queryClient.invalidateQueries({ queryKey: ['myAccountUsers'] });
            refetchUsers();
            setRefreshKey(prev => prev + 1);
        };
        window.addEventListener('user-desvinculado', handleUserDesvinculado);
        return () => window.removeEventListener('user-desvinculado', handleUserDesvinculado);
    }, [refetchUsers, queryClient]);

    // Processador blindado de Usuários
    const processedUsers = useMemo(() => {
        let users = [];
        if (Array.isArray(accountUsersData)) users = accountUsersData;
        else if (accountUsersData && Array.isArray(accountUsersData.data)) users = accountUsersData.data;

        if (users.length === 0) return [];
        
        return users.map(item => {
            const userData = item.user && item.user._id ? item.user : item;
            const roleSource = item.role || userData.role;
            
            let roleName = 'Indefinido';
            let roleId = null;

            if (item.accountVinculo?.role?.id) roleId = item.accountVinculo.role.id;
            else if (typeof roleSource === 'string') roleId = roleSource;
            else if (roleSource?._id) roleId = roleSource._id;
            else if (roleSource?.id) roleId = roleSource.id;

            const possibleNames = [
                item.accountVinculo?.role?.name,
                roleSource?.name,
                item.roleName,
                userData.roleName
            ];
            
            for (const name of possibleNames) {
                if (name && typeof name === 'string' && name.length !== 24 && name !== '[object Object]') {
                    roleName = name;
                    break;
                }
            }

            if (roleName === 'Indefinido' && roleId && rolesList.length > 0) {
                const foundRole = rolesList.find(r => String(r._id || r.id) === String(roleId));
                if (foundRole) {
                    roleName = foundRole.name;
                }
            }

            const humanizedRoles = {
                'matriz_admin': 'Administrador Matriz',
                'franquia_admin': 'Administrador Franquia',
                'conta_admin': 'Administrador da Conta',
                'profissional': 'Profissional',
                'secretaria': 'Secretária(o)',
                'cliente_final': 'Cliente Final',
                'user': 'Usuário Padrão'
            };
            if (humanizedRoles[roleName]) roleName = humanizedRoles[roleName];
            
            // ✅ Usa o status que veio do Vínculo de Conta (AccountUser) do banco de dados
            const statusBanco = item.status || item.statusVinculo || userData.status;
            const isActive = statusBanco !== 'inativo' && userData.isActive !== false;

            return {
                ...userData,
                _id: userData._id || userData.id,
                relationshipId: item._id !== userData._id ? item._id : undefined,
                role: roleId || roleSource,
                roleId: roleId,
                roleName: roleName,
                status: isActive ? 'ativo' : 'inativo',
                isActive: isActive,
                unidades: item.unidades || userData.unidades || [],
                account: item.account || userData.account,
                telefone: userData.telefone || userData.phone || '-',
                photoUrl: userData.photoUrl || userData.photo || 
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=random&color=fff`,
                profissional: userData.profissional || { isProfissional: false }
            };
        }).filter(user => {
            // ✅ FILTRAGEM LOCAL DE TELA
            if (Object.keys(activeFilters).some(key => activeFilters[key] !== '')) {
                return Object.entries(activeFilters).every(([key, value]) => {
                    if (!value || value === '' || value === 'todos') return true; 
                    switch(key) {
                        case 'status':
                            return user.isActive === (value === 'ativo');
                        case 'role':
                            return user.roleName === value;
                        case 'search':
                            const searchTerm = value.toLowerCase();
                            return (
                                (user.name?.toLowerCase() || '').includes(searchTerm) ||
                                (user.telefone?.toLowerCase() || '').includes(searchTerm) ||
                                (user.email?.toLowerCase() || '').includes(searchTerm)
                            );
                        default:
                            return true;
                    }
                });
            }
            return true;
        });
    }, [accountUsersData, rolesList, activeFilters, refreshKey]);

    const uniqueRoles = useMemo(() => {
        const roles = new Set();
        processedUsers.forEach(user => {
            if (user.roleName && user.roleName !== 'Indefinido') {
                roles.add(user.roleName);
            }
        });
        return Array.from(roles).sort();
    }, [processedUsers]);

    const stats = useMemo(() => {
        const ativos = processedUsers.filter(u => u.isActive).length;
        return {
            total: processedUsers.length,
            ativos: ativos,
            inativos: processedUsers.length - ativos
        };
    }, [processedUsers]);

    const totalUsers = processedUsers.length;
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const paginatedUsers = processedUsers.slice(startIndex, endIndex);

    const paginate = (page) => {
        if (page !== currentPage) setCurrentPage(page);
    };

    const openFormForNew = useCallback(() => {
        if (!contaId) {
            setAlert('Erro: Conta não identificada', 'danger', 3000);
            return;
        }
        navigate('/minha-conta/usuarios/novo', {
            state: { 
                isCreatingMode: true,
                contaId,
                allowedRoles: ['conta_admin', 'profissional', 'secretaria', 'cliente_final'],
                defaultRole: 'profissional'
            }
        });
    }, [navigate, contaId, setAlert]);

    const handleEdit = useCallback((user) => {
        if (user._id) {
            navigate(`/minha-conta/usuarios/${user._id}/editar`, {
                state: {
                    isCreatingMode: false,
                    contaId,
                    userId: user._id,
                    userName: user.name,
                    allowedRoles: ['conta_admin', 'profissional', 'secretaria', 'cliente_final'],
                    defaultRole: user.roleName || 'profissional'
                }
            });
        }
        setSelectedUser(null);
    }, [navigate, contaId]);

    const handleToggleStatus = useCallback((user) => {
        if (user.fakeToggle) {
             queryClient.invalidateQueries({ queryKey: ['accountUsers'] });
             queryClient.invalidateQueries({ queryKey: ['myAccountUsers'] });
             refetchUsers();
             setRefreshKey(prev => prev + 1);
             return;
        }

        if (window.confirm(`Deseja ${user.isActive ? "desativar" : "reativar"} o acesso de ${user.name}?`)) {
            toggleUserActiveStatus.mutate({
                userId: user._id,
                accountId: contaId,
                isActive: !user.isActive
            }, {
                onSuccess: () => {
                    setAlert('Status alterado com sucesso!', 'success', 3000);
                    refetchUsers();
                },
                onError: (error) => {
                    setAlert(error.response?.data?.error || 'Erro ao alterar status', 'danger', 5000);
                }
            });
        }
        setSelectedUser(null);
    }, [toggleUserActiveStatus, setAlert, refetchUsers, contaId, queryClient]);

    const handleResetPassword = useCallback((userId, userName) => {
        if (window.confirm(`Resetar senha para ${userName}?`)) {
            resetAccountUserPassword.mutate({ userId, accountId: contaId }, {
                onSuccess: (data) => {
                    setAlert('Senha resetada com sucesso!', 'success', 3000);
                    if (data?.temporaryPassword) {
                        setTimeout(() => setAlert(`Senha temporária: ${data.temporaryPassword}`, 'info', 10000), 500);
                    }
                }
            });
        }
        setSelectedUser(null);
    }, [resetAccountUserPassword, setAlert, contaId]);

    // ✅ FILTROS PADRONIZADOS
    const handleFilterChange = (e) => {
        setLocalFilters({ ...localFilters, [e.target.name]: e.target.value });
    };

    const handleApplyFilters = useCallback((e) => { 
        if (e) e.preventDefault();
        console.log('🔍 Aplicando filtros:', localFilters);
        setActiveFilters(localFilters);
        setCurrentPage(1); 
    }, [localFilters]);

    const handleClearFilters = useCallback(() => {
        setLocalFilters(initialFilters);
        setActiveFilters(initialFilters);
        setCurrentPage(1);
    }, [initialFilters]);

    const hasFilters = Object.values(localFilters).some(v => v !== '' && v !== 'todos');

    if (accountLoading && processedUsers.length === 0) {
        return (
            <div className="page-management-container">
                <div className="page-header"><h1 className="page-title"><Users className="icon-title mr-2"/>Gerenciar Equipe</h1></div>
                <div className="flex justify-center items-center py-12">
                    <Loader2 size={32} className="animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <PermissionGuard requiredPermission="equipe:view">
            <div className="page-management-container">
                <PageHeader
                    icon={<Users />}
                    title="Gerenciamento de Equipe"
                    actions={<>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                        >
                            <FilterIcon size={18} className="icon"/>
                            {showFilters ? 'Ocultar' : 'Exibir'} Filtros
                        </button>
                    </>}
                />

                {/* ✅ FILTRO 100% PADRONIZADO */}
                {showFilters && (
                    <div className="content-panel mb-4 p-0 bg-transparent border-0 shadow-none">
                        <form onSubmit={handleApplyFilters} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-search" className="text-xs font-semibold text-gray-600 uppercase">Buscar Nome ou Telefone</label>
                                    <div className="relative">
                                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input 
                                            type="text" 
                                            id="filter-search"
                                            name="search"
                                            value={localFilters.search} 
                                            onChange={handleFilterChange} 
                                            placeholder="Buscar..." 
                                            className="form-input text-sm py-2 px-3 pl-10 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-role" className="text-xs font-semibold text-gray-600 uppercase">Função</label>
                                    <select 
                                        id="filter-role"
                                        name="role"
                                        value={localFilters.role} 
                                        onChange={handleFilterChange} 
                                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full"
                                    >
                                        <option value="">Todas as funções</option>
                                        {uniqueRoles.map(role => <option key={role} value={role}>{role}</option>)}
                                    </select>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-status" className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                                    <select 
                                        id="filter-status"
                                        name="status"
                                        value={localFilters.status} 
                                        onChange={handleFilterChange} 
                                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full"
                                    >
                                        <option value="todos">Todos</option>
                                        <option value="ativo">Ativos</option>
                                        <option value="inativo">Inativos</option>
                                    </select>
                                </div>

                                {/* Antes reinventava cor do zero em Tailwind (bg-blue-600)
                                    e o texto "Filtrar" so aparecia acima de 1280px */}
                                <div className="flex gap-2 h-full">
                                    <button
                                        type="submit"
                                        className="action-button action-button-primary flex-1 justify-center"
                                        title="Aplicar Filtros"
                                    >
                                        <Search size={18} className="icon" />
                                        Aplicar Filtros
                                    </button>

                                    {hasFilters && (
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="action-button action-button-secondary justify-center"
                                            title="Limpar Filtros"
                                        >
                                            <X size={18} className="icon" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* EquipePanel agora ja se envolve em table-container sozinho
                    — o wrapper aqui fora causava um card duplicado/aninhado */}
                <EquipePanel
                    users={paginatedUsers}
                    isLoading={accountLoading}
                    onOpenActions={setSelectedUser}
                />

                {totalUsers > usersPerPage && !accountLoading && (
                    <Pagination
                        itemsPerPage={usersPerPage}
                        totalItems={totalUsers}
                        onPageChange={paginate}
                        currentPage={currentPage}
                    />
                )}
            </div>

            <FabButton onClick={openFormForNew} title="Adicionar Usuário" icon={<UserPlus size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedUser}
                onClose={() => setSelectedUser(null)}
                title={selectedUser?.name || 'Usuário'}
                subtitle={selectedUser?.roleName}
                actions={selectedUser ? [
                    {
                        key: 'editar',
                        label: 'Visualizar / Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEdit(selectedUser),
                    },
                    ...(selectedUser.status !== 'excluido' ? [{
                        key: 'resetar-senha',
                        label: 'Resetar Senha',
                        icon: <Key size={20} className="icon" />,
                        onClick: () => handleResetPassword(selectedUser._id || selectedUser.id, selectedUser.name),
                    }] : []),
                    ...(selectedUser.status !== 'excluido' ? [{
                        key: 'toggle-status',
                        label: selectedUser.isActive ? 'Desativar Acesso' : 'Reativar Acesso',
                        icon: selectedUser.isActive ? <UserX size={20} className="icon" /> : <UserCheck size={20} className="icon" />,
                        onClick: () => handleToggleStatus(selectedUser),
                    }] : []),
                ] : []}
            />
        </PermissionGuard>
    );
};

export default AccountUsers;